/**
 * The Agents page's pure rules: what each agent card says, read from the
 * stores the rest of the app already keeps — the chat list (thread shells),
 * the Inspector's run history and schedule, the findings queue, Probe's saved
 * interviews and the automations list. Nothing is sampled or estimated.
 *
 * @module components/agents/agentsLogic
 */
import type { EnvironmentThreadShell } from "@amoreg/client-runtime/state/models";
import {
  describeInspectionSchedule,
  nextInspectionRun,
  type InspectionSchedule,
} from "@repo/shared/inspectionSchedule";

import type { InspectionRun } from "../../state/inspectionSchemas";
import type { AutomationView } from "../../state/automations";
import type { FindingItem } from "../regulation/finding/findingItem";
import { recordedSummary } from "../regulation/inspector/InspectionRunRow";

const WEEK_MS = 7 * 24 * 60 * 60 * 1000;
const INSPECTOR_INSTANCE_ID = "amoreg-inspector";

const timeMs = (iso: string | null | undefined): number | null => {
  const parsed = iso ? Date.parse(iso) : Number.NaN;
  return Number.isNaN(parsed) ? null : parsed;
};

/**
 * Chats with a message sent in the last seven days, Inspector's runs aside
 * (they are its threads, not anyone's chats). The caller passes the
 * organization's chats (`selectOrgThreads`): the ones this person can see.
 */
export function chatsThisWeek(
  threads: ReadonlyArray<
    Pick<EnvironmentThreadShell, "latestUserMessageAt" | "createdAt" | "modelSelection">
  >,
  nowMs: number,
): number {
  const since = nowMs - WEEK_MS;
  return threads.filter((thread) => {
    if (thread.modelSelection.instanceId === INSPECTOR_INSTANCE_ID) return false;
    const at = timeMs(thread.latestUserMessageAt) ?? timeMs(thread.createdAt);
    return at !== null && at >= since && at <= nowMs;
  }).length;
}

export interface InspectorCadence {
  /** "Daily at 07:00 UTC", "Off", or why it is off. */
  readonly label: string;
  /** The next run: a booked one first, else the schedule's; null when nothing will run. */
  readonly nextAt: Date | null;
  readonly booked: boolean;
}

export function inspectorCadence(input: {
  readonly schedule: InspectionSchedule | null;
  readonly monitoringEnabled: boolean | null;
  readonly bookedAt: string | null;
  readonly nowMs: number;
}): InspectorCadence | null {
  const booked = timeMs(input.bookedAt);
  if (input.schedule === null || input.monitoringEnabled === null) {
    return booked === null ? null : { label: "Booked run", nextAt: new Date(booked), booked: true };
  }
  const label = !input.monitoringEnabled
    ? "Off while regulatory monitoring is off"
    : describeInspectionSchedule(input.schedule);
  if (booked !== null) return { label, nextAt: new Date(booked), booked: true };
  const next =
    input.monitoringEnabled && input.schedule.cadence !== "off"
      ? nextInspectionRun(input.schedule, input.nowMs)
      : null;
  return { label, nextAt: next, booked: false };
}

export interface InspectorLastRun {
  readonly run: InspectionRun;
  /** When it ended, or started if it has not. */
  readonly at: string;
  /** "Completed · 3 findings, 1 suggestion", "Failed", "Running". */
  readonly result: string;
}

/**
 * What a run recorded, in the Inspector's own words (recordedSummary: findings,
 * suggestions, publications, revisions, withdrawals); "nothing raised" only
 * for a run that recorded nothing at all.
 */
export function inspectionFoundSummary(found: InspectionRun["found"]): string {
  return recordedSummary(found) || "nothing raised";
}

const STATUS_WORD: Record<InspectionRun["status"], string> = {
  queued: "Queued",
  running: "Running",
  completed: "Completed",
  failed: "Failed",
  interrupted: "Stopped",
};

/** The newest run, whatever its state — the card says how it ended. */
export function inspectorLastRun(runs: ReadonlyArray<InspectionRun>): InspectorLastRun | null {
  const latest = runs.toSorted(
    (left, right) => (timeMs(right.startedAt) ?? 0) - (timeMs(left.startedAt) ?? 0),
  )[0];
  if (latest === undefined) return null;
  return {
    run: latest,
    at: latest.completedAt ?? latest.startedAt,
    result:
      latest.status === "completed"
        ? `${STATUS_WORD.completed} · ${inspectionFoundSummary(latest.found)}`
        : STATUS_WORD[latest.status],
  };
}

/** Open findings the Inspector raised, from the findings queue's own items. */
export const inspectorOpenFindings = (
  items: ReadonlyArray<Pick<FindingItem, "status" | "raisedBy" | "origin">>,
) =>
  items.filter(
    (item) => item.origin === "finding" && item.status === "open" && item.raisedBy === "Inspector",
  ).length;

export interface AutomationSummary {
  /** Switched on and with a run still to come. */
  readonly active: number;
  readonly total: number;
  readonly next: { readonly title: string; readonly at: string; readonly label: string } | null;
}

/** How many automations will run, and which runs next. */
export function automationSummary(automations: ReadonlyArray<AutomationView>): AutomationSummary {
  const live = automations.filter((automation) => automation.enabled && !automation.finished);
  const next = live
    .filter((automation) => timeMs(automation.nextRunAt) !== null)
    .toSorted((left, right) => (timeMs(left.nextRunAt) ?? 0) - (timeMs(right.nextRunAt) ?? 0))[0];
  return {
    active: live.length,
    total: automations.length,
    next: next ? { title: next.title, at: next.nextRunAt, label: next.nextRunLabel } : null,
  };
}

/** "in 3 h", "in 2 days", "now": how far away a run is. */
export function relativeFuture(atMs: number, nowMs: number): string {
  const minutes = Math.round((atMs - nowMs) / 60_000);
  if (minutes <= 0) return "now";
  if (minutes < 60) return `in ${minutes} min`;
  const hours = Math.round(minutes / 60);
  if (hours < 48) return `in ${hours} h`;
  return `in ${Math.round(hours / 24)} days`;
}

/**
 * The Automations card's "Next run": how far away the next run is, "None",
 * "…" while the list loads, or "—" once it cannot load (never "…" forever).
 */
export function automationNextRun(
  state: AutomationSummary | null,
  unavailable: boolean,
  nowMs: number,
): string {
  if (state === null) return unavailable ? "—" : "…";
  return state.next ? relativeFuture(Date.parse(state.next.at), nowMs) : "None";
}
